import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { COUNTRIES } from "../lib/countries";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, ShieldCheck, ShieldAlert, Link as LinkIcon, KeyRound, Trash2, AlertCircle, UserCog, AtSign } from "lucide-react";
import { Link } from "react-router-dom";
import {
    AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const PRIMARY_BTN =
    "w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:brightness-95 hover:shadow-glow transition-[filter,box-shadow] duration-150 disabled:opacity-50 disabled:hover:shadow-none";
const GHOST_BTN =
    "w-full rounded-full border border-line-strong bg-surface-raised py-3 text-sm font-semibold text-foreground hover:border-accent/50 hover:text-accent transition-colors duration-150 disabled:opacity-50";
const ERROR_BOX =
    "flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3 text-sm text-neg";
const SUCCESS_BOX =
    "rounded-xl border border-pos/30 bg-pos/[0.07] p-3.5";

function SectionCard({ icon: Icon, title, description, children, tone = "default", ...rest }) {
    const accentTone = tone === "danger" ? "bg-neg/10 text-neg" : "bg-accent-soft text-accent";
    return (
        <section className={`panel p-6 sm:p-7 ${tone === "danger" ? "border-neg/30" : ""}`} {...rest}>
            <div className="flex items-start gap-3.5 mb-6">
                <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ${accentTone}`}>
                    <Icon size={18} />
                </span>
                <div>
                    <h2 className={`font-display text-lg font-bold ${tone === "danger" ? "text-neg" : ""}`}>
                        {title}
                    </h2>
                    {description && <p className="text-sm text-dim mt-1 leading-relaxed">{description}</p>}
                </div>
            </div>
            {children}
        </section>
    );
}

export default function AccountPage() {
    const { user, setUser, refreshUser } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState(null);
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState(null);
    const [profileErr, setProfileErr] = useState("");

    const [newEmail, setNewEmail] = useState("");
    const [savingEmail, setSavingEmail] = useState(false);
    const [emailMsg, setEmailMsg] = useState(null);
    const [emailErr, setEmailErr] = useState("");

    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [savingPw, setSavingPw] = useState(false);
    const [pwMsg, setPwMsg] = useState("");
    const [pwErr, setPwErr] = useState("");

    const [delPassword, setDelPassword] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [delErr, setDelErr] = useState("");

    useEffect(() => {
        if (user === false) navigate("/login");
    }, [user, navigate]);

    useEffect(() => {
        if (user && !form) {
            const c = COUNTRIES.find((x) => x.name === user.country);
            setForm({
                name: user.name || "",
                countryName: user.country || "United States",
                dial: user.dial_code || (c ? c.dial : "+1"),
                phone: user.phone || "",
                consent_transactional: !!user.consent_transactional,
                consent_marketing: !!user.consent_marketing,
            });
        }
    }, [user, form]);

    if (!user || !form) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-24 text-center">
                <p className="inline-flex items-center gap-2 font-mono text-sm text-dim">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                    Loading your account…
                </p>
            </div>
        );
    }

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const onCountry = (name) => {
        const c = COUNTRIES.find((x) => x.name === name);
        setForm((f) => ({ ...f, countryName: name, dial: c ? c.dial : f.dial }));
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        setProfileErr(""); setProfileMsg(null); setSavingProfile(true);
        try {
            const { data } = await api.put("/auth/profile", {
                name: form.name,
                country: form.countryName,
                dial_code: form.dial,
                phone: form.phone,
                consent_transactional: form.consent_transactional,
                consent_marketing: form.consent_marketing,
            });
            setUser(data);
            setProfileMsg("Your preferences have been saved.");
        } catch (err) {
            setProfileErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingProfile(false);
        }
    };

    const saveEmail = async (e) => {
        e.preventDefault();
        setEmailErr(""); setEmailMsg(null); setSavingEmail(true);
        try {
            const { data } = await api.post("/auth/change-email", { email: newEmail });
            setUser(data);
            setEmailMsg({ text: "Email updated. Please confirm it to re-activate your subscription.", dev: data.dev_verify_url });
            setNewEmail("");
        } catch (err) {
            setEmailErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingEmail(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        setPwErr(""); setPwMsg("");
        if (pw.next !== pw.confirm) { setPwErr("New passwords do not match."); return; }
        setSavingPw(true);
        try {
            const { data } = await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
            setPwMsg(data.message);
            setPw({ current: "", next: "", confirm: "" });
        } catch (err) {
            setPwErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingPw(false);
        }
    };

    const deleteAccount = async () => {
        setDelErr(""); setDeleting(true);
        try {
            await api.post("/auth/delete-account", { password: delPassword });
            setUser(false);
            navigate("/");
        } catch (err) {
            setDelErr(formatApiError(err.response?.data?.detail) || err.message);
            setDeleting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-10" data-testid="account-page">
            <div className="pb-7 border-b border-line">
                <span className="block h-0.5 w-10 rounded-full bg-accent mb-5" />
                <span className="eyebrow text-accent">Your account</span>
                <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2.5">
                    Account settings
                </h1>
            </div>

            {/* Verification status */}
            <div
                className={`flex items-center gap-3.5 rounded-xl border p-4 ${
                    user.email_verified ? "border-pos/30 bg-pos/[0.07]" : "border-neg/30 bg-neg/[0.07]"
                }`}
                data-testid="account-verify-status"
            >
                {user.email_verified
                    ? <ShieldCheck className="text-pos shrink-0" size={20} />
                    : <ShieldAlert className="text-neg shrink-0" size={20} />}
                <span className="text-sm text-dim leading-relaxed">
                    {user.email_verified
                        ? <>Your email <span className="font-mono text-foreground">{user.email}</span> is confirmed.</>
                        : <>Your email <span className="font-mono text-foreground">{user.email}</span> is not confirmed yet. You won't receive newsletters until you confirm it.</>}
                </span>
            </div>

            {/* Profile & preferences */}
            <SectionCard icon={UserCog} title="Profile & contact preferences">
                <form onSubmit={saveProfile} className="space-y-5">
                    <div>
                        <label className="eyebrow text-dim block mb-2">Full name</label>
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="account-name" className="field" />
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Country</label>
                        <Select value={form.countryName} onValueChange={onCountry}>
                            <SelectTrigger data-testid="account-country" className="field h-auto shadow-none">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border border-line bg-popover max-h-72 shadow-lift">
                                {COUNTRIES.map((c) => (
                                    <SelectItem key={c.code} value={c.name} className="rounded-lg font-sans text-sm">
                                        {c.name} <span className="font-mono text-xs text-dim">({c.dial})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Phone number</label>
                        <div className="flex gap-2">
                            <span
                                className="inline-flex items-center rounded-[0.625rem] border border-line bg-surface-raised px-3.5 font-mono text-sm text-dim shrink-0"
                                data-testid="account-dial"
                            >
                                {form.dial}
                            </span>
                            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="account-phone" className="field !font-mono" />
                        </div>
                    </div>
                    <div className="space-y-4 rounded-xl border border-line bg-surface-sunken p-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                                checked={form.consent_transactional}
                                onCheckedChange={(v) => set("consent_transactional", !!v)}
                                data-testid="account-consent-transactional"
                                className="rounded-[5px] border-line-strong mt-0.5 shadow-none"
                            />
                            <span className="text-sm leading-snug text-dim">
                                Receive transactional messages (account &amp; service notifications) by email and SMS.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                                checked={form.consent_marketing}
                                onCheckedChange={(v) => set("consent_marketing", !!v)}
                                data-testid="account-consent-marketing"
                                className="rounded-[5px] border-line-strong mt-0.5 shadow-none"
                            />
                            <span className="text-sm leading-snug text-dim">
                                Receive marketing messages (newsletters &amp; offers) by email and SMS.
                            </span>
                        </label>
                        {!user.email_verified && form.consent_marketing && (
                            <p className="text-[11px] text-neg">Marketing delivery starts once your email is confirmed.</p>
                        )}
                    </div>
                    {profileErr && (
                        <p className={ERROR_BOX} data-testid="account-profile-error">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {profileErr}
                        </p>
                    )}
                    {profileMsg && (
                        <div className={`${SUCCESS_BOX} flex items-center gap-2.5`} data-testid="account-profile-success">
                            <CheckCircle2 className="text-pos shrink-0" size={17} />
                            <span className="text-sm text-dim">{profileMsg}</span>
                        </div>
                    )}
                    <button type="submit" disabled={savingProfile} data-testid="account-save-profile" className={PRIMARY_BTN}>
                        {savingProfile ? "Saving…" : "Save preferences"}
                    </button>
                </form>
            </SectionCard>

            {/* Email address */}
            <SectionCard
                icon={AtSign}
                title="Email address"
                description="Changing your email requires re-confirming the new address before newsletters resume."
            >
                <form onSubmit={saveEmail} className="space-y-4">
                    <div>
                        <label className="eyebrow text-dim block mb-2">New email</label>
                        <input
                            type="email"
                            required
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={user.email}
                            data-testid="account-new-email"
                            className="field"
                        />
                    </div>
                    {emailErr && (
                        <p className={ERROR_BOX} data-testid="account-email-error">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {emailErr}
                        </p>
                    )}
                    {emailMsg && (
                        <div className={SUCCESS_BOX} data-testid="account-email-success">
                            <div className="flex items-center gap-2.5">
                                <CheckCircle2 className="text-pos shrink-0" size={17} />
                                <span className="text-sm text-dim">{emailMsg.text}</span>
                            </div>
                            {emailMsg.dev && (
                                <Link
                                    to={emailMsg.dev.replace(/^https?:\/\/[^/]+/, "")}
                                    data-testid="account-dev-verify-link"
                                    className="mt-2.5 inline-flex items-center gap-1.5 text-sm text-accent link-underline break-all"
                                >
                                    <LinkIcon size={13} /> Confirm your new email now →
                                </Link>
                            )}
                        </div>
                    )}
                    <button type="submit" disabled={savingEmail} data-testid="account-save-email" className={GHOST_BTN}>
                        {savingEmail ? "Updating…" : "Update email"}
                    </button>
                </form>
            </SectionCard>

            {/* Change password */}
            <SectionCard
                icon={KeyRound}
                title="Change password"
                description="Choose a strong new password for signing in."
            >
                <form onSubmit={savePassword} className="space-y-4">
                    <div>
                        <label className="eyebrow text-dim block mb-2">Current password</label>
                        <input
                            type="password"
                            required
                            value={pw.current}
                            onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                            data-testid="account-current-password"
                            className="field"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="eyebrow text-dim block mb-2">New password</label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={pw.next}
                                onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                                data-testid="account-new-password"
                                className="field"
                            />
                        </div>
                        <div>
                            <label className="eyebrow text-dim block mb-2">Confirm new</label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={pw.confirm}
                                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                                data-testid="account-confirm-password"
                                className="field"
                            />
                        </div>
                    </div>
                    {pwErr && (
                        <p className={ERROR_BOX} data-testid="account-password-error">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {pwErr}
                        </p>
                    )}
                    {pwMsg && (
                        <div className={`${SUCCESS_BOX} flex items-center gap-2.5`} data-testid="account-password-success">
                            <CheckCircle2 className="text-pos shrink-0" size={17} />
                            <span className="text-sm text-dim">{pwMsg}</span>
                        </div>
                    )}
                    <button type="submit" disabled={savingPw} data-testid="account-save-password" className={PRIMARY_BTN}>
                        {savingPw ? "Updating…" : "Update password"}
                    </button>
                </form>
            </SectionCard>

            {/* Danger zone */}
            {user.role !== "admin" && (
                <SectionCard
                    icon={Trash2}
                    tone="danger"
                    title="Delete account"
                    description="This permanently removes your account, profile and marketing preferences. This cannot be undone."
                    data-testid="account-danger-zone"
                >
                    <label className="eyebrow text-dim block mb-2">Confirm your password</label>
                    <input
                        type="password"
                        value={delPassword}
                        onChange={(e) => setDelPassword(e.target.value)}
                        data-testid="account-delete-password"
                        className="field mb-4"
                    />
                    {delErr && (
                        <p className={`${ERROR_BOX} mb-4`} data-testid="account-delete-error">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {delErr}
                        </p>
                    )}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button
                                type="button"
                                disabled={!delPassword || deleting}
                                data-testid="account-delete-trigger"
                                className="w-full rounded-full bg-neg text-canvas py-3 text-sm font-semibold hover:brightness-95 transition-[filter] duration-150 disabled:opacity-50"
                            >
                                {deleting ? "Deleting…" : "Delete my account"}
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent
                            className="rounded-2xl border border-line bg-surface shadow-lift"
                            data-testid="account-delete-dialog"
                        >
                            <AlertDialogHeader>
                                <AlertDialogTitle className="font-display text-xl font-bold">
                                    Delete your account?
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-sm text-dim leading-relaxed">
                                    This will permanently delete your account and all associated data. You can't undo this.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel
                                    className="rounded-full border-line bg-surface-raised text-foreground text-sm font-medium hover:bg-surface-raised hover:text-accent"
                                    data-testid="account-delete-cancel"
                                >
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={deleteAccount}
                                    data-testid="account-delete-confirm"
                                    className="rounded-full bg-neg text-canvas text-sm font-semibold hover:bg-neg hover:brightness-95"
                                >
                                    Yes, delete everything
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </SectionCard>
            )}
        </div>
    );
}
